import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Instagram } from "lucide-react";
import { api, imgUrl, money } from "../lib/api";
import { MaskedLines, FadeUp, SectionHead } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { ArtImage } from "../components/ArtImage";
import { usePageTitle } from "../hooks/usePageTitle";

export const PrintCard = ({ print, index }) => {
  const soldOut = print.edition_remaining === 0;
  return (
    <Link to={`/prints/${print.id}`} data-testid={`print-card-${print.id}`} className="group block border border-white/10 bg-charcoal/40">
      <div className="relative overflow-hidden">
        <ArtImage art={print} size="md" sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.02]" testId={`print-card-image-${index}`} />
        {soldOut && (
          <span data-testid={`print-sold-out-${index}`} className="absolute top-3 left-3 bg-ink border border-pink text-pink text-[10px] font-extrabold tracking-[0.25em] px-3 py-1.5">
            SOLD OUT
          </span>
        )}
      </div>
      <div className="p-4 flex items-start justify-between gap-3">
        <div>
          <p className="font-display text-xl tracking-wide leading-none">{print.title || "UNTITLED"}</p>
          <p className="text-xs text-smoke mt-1.5">
            {print.type === "digital" ? "DIGITAL DOWNLOAD" : print.type === "open" ? "OPEN EDITION PRINT" : print.edition_size ? `EDITION OF ${print.edition_size}` : "SIGNED + NUMBERED"}
          </p>
        </div>
        <p className="font-display text-lg text-bone shrink-0">{money(print.price) || "PRICE TBA"}</p>
      </div>
    </Link>
  );
};

export default function Home({ config }) {
  usePageTitle(null);
  const [artworks, setArtworks] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/artworks").then((r) => setArtworks(r.data)).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data.filter((e) => !e.past).slice(0, 3))).catch(() => {});
  }, []);

  const muralArts = [artworks[1], artworks[2]].filter(Boolean);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative pt-32 sm:pt-36 px-5 sm:px-8 overflow-hidden" data-testid="hero-section">
        <div className="lg:min-h-[72svh] lg:pb-16 flex flex-col justify-center">
          <div>
            <div className="flex items-center gap-2 mb-5">
              <span className="w-2 h-2 bg-pink" />
              <span className="text-[10px] sm:text-xs font-bold tracking-[0.3em] text-smoke">KENNY DEPRINCE — VISUAL ARTIST</span>
            </div>
            <h1 className="font-display leading-[0.85] tracking-wide" data-testid="hero-headline">
              <MaskedLines
                lines={[
                  <span key="a" className="text-[23vw] sm:text-8xl lg:text-[7.5rem] xl:text-[9rem]">ART</span>,
                  <span key="b" className="text-[23vw] sm:text-8xl lg:text-[7.5rem] xl:text-[9rem] text-outline">WITHOUT</span>,
                  <span key="c" className="text-[23vw] sm:text-8xl lg:text-[7.5rem] xl:text-[9rem]">PERMISSION<span className="text-pink">.</span></span>,
                ]}
              />
            </h1>
            <FadeUp delay={0.55} className="mt-6 max-w-md">
              <p className="text-base sm:text-lg text-bone/80 leading-relaxed" data-testid="hero-subcopy">
                Original works, limited-edition prints and custom murals by Kenny DePrince.
              </p>
            </FadeUp>
            <FadeUp delay={0.7} className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/art" data-testid="hero-explore-art" className="h-12 px-7 bg-bone text-ink text-sm font-extrabold tracking-[0.18em] flex items-center gap-2 hover:bg-pink hover:text-white transition-colors duration-200">
                EXPLORE THE ART <ArrowRight size={16} />
              </Link>
              <Link to="/prints" data-testid="hero-shop-prints" className="h-12 px-7 border border-white/25 text-sm font-extrabold tracking-[0.18em] flex items-center hover:border-pink hover:text-pink transition-colors duration-200">
                SHOP PRINTS
              </Link>
            </FadeUp>
            <FadeUp delay={0.8} className="mt-5 max-lg:pb-16">
              <Link to="/murals" data-testid="hero-commission-mural" className="inline-flex items-center gap-2 text-sm font-bold tracking-[0.15em] text-pink border-b border-pink/40 pb-1 min-h-[44px]">
                COMMISSION A MURAL <ArrowUpRight size={16} />
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      <Marquee />

      {/* 01 THE ARTIST */}
      <section className="px-5 sm:px-8 py-16 sm:py-24" data-testid="artist-section">
        <SectionHead index="01" title="MEET KENNY DEPRINCE" />
        <FadeUp className="mt-8 max-w-2xl">
          {config?.artist_photo ? (
            <div className="border border-white/15 overflow-hidden aspect-[3/2]" data-testid="artist-portrait">
              <img src={imgUrl(config.artist_photo)} alt="Kenny DePrince — artist, ARTILLERY KDP" className="w-full h-full object-cover object-left" loading="lazy" draggable={false} />
            </div>
          ) : (
            <div className="aspect-[3/2] border border-dashed border-white/20 flex items-center justify-center p-4" data-testid="artist-portrait-placeholder">
              <span className="text-[10px] tracking-[0.3em] text-smoke text-center leading-relaxed">ARTIST PORTRAIT<br />COMING SOON</span>
            </div>
          )}
        </FadeUp>
        <div className="mt-8 max-w-xl space-y-5">
          {(config?.about_long || "").split("\n\n").filter(Boolean).map((p, i) => (
            <FadeUp key={i} delay={Math.min(i * 0.05, 0.2)}>
              <p className={`leading-relaxed ${i === 0 ? "text-lg text-bone font-semibold" : "text-base text-bone/80"}`} data-testid={`home-bio-${i}`}>{p}</p>
            </FadeUp>
          ))}
        </div>
        <FadeUp className="mt-8 flex flex-wrap gap-2" data-testid="home-artist-chips">
          {["STENCIL ART", "POP ART", "STREET INFLUENCE", "PHILADELPHIA", "ORIGINALS", "LIMITED EDITIONS", "MURALS"].map((c) => (
            <span key={c} className="text-[10px] font-bold tracking-[0.2em] border border-white/15 text-bone/70 px-3 py-2">{c}</span>
          ))}
        </FadeUp>
        <FadeUp className="mt-8">
          <Link to="/about" data-testid="home-about-kenny" className="h-12 px-7 inline-flex items-center gap-2 bg-bone text-ink text-sm font-extrabold tracking-[0.18em] hover:bg-pink hover:text-white transition-colors duration-200">
            ABOUT KENNY <ArrowRight size={16} />
          </Link>
        </FadeUp>
      </section>

      {/* 02 MURALS */}
      <section className="px-5 sm:px-8 py-16 sm:py-24 border-t border-white/10" data-testid="murals-section">
        <SectionHead index="02" title="MURALS" />
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {["COMMERCIAL MURALS", "RESIDENTIAL MURALS"].map((label, i) => (
            <FadeUp key={label} delay={i * 0.1}>
              <Link to={`/murals?type=${label.split(" ")[0].toLowerCase()}`} data-testid={`home-mural-${label.split(" ")[0].toLowerCase()}`}
                className="group relative block h-56 sm:h-72 overflow-hidden border border-white/10">
                {muralArts[i] && (
                  <ArtImage art={muralArts[i]} size="md" sizes="(max-width: 640px) 100vw, 50vw"
                    className="absolute inset-0 w-full h-full object-cover opacity-45 transition-all duration-500 group-hover:opacity-60 group-hover:scale-[1.03]" testId={`home-mural-image-${i}`} />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-5 flex items-end justify-between">
                  <span className="font-display text-3xl sm:text-4xl tracking-wide leading-none">{label}</span>
                  <ArrowUpRight className="text-pink mb-1" size={22} />
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
        <FadeUp className="mt-8">
          <Link to="/murals" data-testid="home-explore-murals" className="h-12 px-7 inline-flex items-center gap-2 bg-bone text-ink text-sm font-extrabold tracking-[0.18em] hover:bg-pink hover:text-white transition-colors duration-200">
            EXPLORE MURALS <ArrowRight size={16} />
          </Link>
        </FadeUp>
      </section>

      {/* 03 EVENTS */}
      <section className="px-5 sm:px-8 py-16 sm:py-24 border-t border-white/10" data-testid="events-section">
        <SectionHead index="03" title="SEE ARTILLERY KDP IN PERSON" />
        <div className="mt-8 divide-y divide-white/10 border-y border-white/10">
          {events.length === 0 && <p className="py-6 text-sm text-smoke">Upcoming shows to be announced.</p>}
          {events.map((e, i) => (
            <FadeUp key={e.id} className="py-5 flex items-baseline justify-between gap-4" data-testid={`home-event-${i}`}>
              <div>
                <p className="font-display text-xl sm:text-2xl tracking-wide leading-none">{e.name}</p>
                <p className="text-xs text-smoke mt-1.5">{e.date || "DATE TO BE CONFIRMED"}{e.location ? ` — ${e.location}` : ""}</p>
              </div>
              <span className="font-display text-smoke text-lg shrink-0">{String(i + 1).padStart(2, "0")}</span>
            </FadeUp>
          ))}
        </div>
        <FadeUp className="mt-8">
          <Link to="/events" data-testid="home-view-events" className="h-12 px-7 inline-flex items-center gap-2 border border-white/25 text-sm font-extrabold tracking-[0.18em] hover:border-pink hover:text-pink transition-colors duration-200">
            VIEW EVENTS <ArrowRight size={16} />
          </Link>
        </FadeUp>
      </section>

      {/* INSTAGRAM */}
      <section className="px-5 sm:px-8 py-16 border-t border-white/10" data-testid="instagram-section">
        <FadeUp className="flex items-end justify-between gap-4">
          <h2 className="font-display text-3xl sm:text-4xl tracking-wide leading-none">FOLLOW THE WORK</h2>
          <Instagram className="text-pink" size={22} />
        </FadeUp>
        <FadeUp className="mt-6">
          <a href={config?.instagram || "https://instagram.com/swingkidd"} target="_blank" rel="noopener noreferrer"
            data-testid="instagram-follow-button"
            className="h-12 px-7 inline-flex items-center gap-2 border border-white/25 text-sm font-extrabold tracking-[0.18em] hover:border-pink hover:text-pink transition-colors duration-200">
            FOLLOW ARTILLERY KDP ON INSTAGRAM <ArrowUpRight size={16} />
          </a>
        </FadeUp>
      </section>
    </div>
  );
}
